/**
 * 🧍 **Who can see whom** — T173, protocol 26. Pure, and that is the point.
 *
 * 🎯 **haugur, 2026-09-07:** *"we need to build a caracter so people can see
 * each other in the game. if not people would only see block appearing but no
 * one building them."*
 *
 * ## 🔴 Why this is not in `server/index.ts` beside the broadcast
 *
 * ⚠️ **`server/index.ts` cannot be imported without a database.** It pulls in
 * `db.ts`, which refuses to load without a connection string, **so a test file
 * beside it does not run on this workstation at all** — which is exactly how the
 * container query shipped with a float in it, and how a message shipped that the
 * parser hung up on. **The rule that keeps coming out of those: if a decision
 * can be arithmetic over plain values, put it where a test can reach it.**
 *
 * ⚠️ **This decides WHO is in the list, not how often it is sent.** The beat and
 * the "say nothing if nothing changed" live with the socket, because they are
 * about the socket.
 */

/** One connected player, as much of them as presence cares about. */
export interface Present {
  /** The connection's own id — see `PlayersMessage`. Never shown to anybody. */
  readonly id: string;
  /**
   * 🧍 **What to call this person on screen** — T176, protocol 27, or `null`
   * for a connection that has no account behind it.
   *
   * 🔴 **`null` IS THE ORDINARY CASE ON DEVELOPMENT, not an error.** Dev runs
   * `ALLOW_ANONYMOUS_BUILD=true` with **zero rows in `users`**, so every
   * connection there genuinely has no name. ⚠️ **The client must draw nothing
   * at all for a `null`** — *Anonymous* over a head is worse than a bare head,
   * because it invents a person who does not exist.
   *
   * ⚠️ **An account, unlike {@link id}, which is a connection.** Two windows
   * open on one account are two bodies wearing the same name, and that is
   * correct: they are the same person twice.
   */
  readonly name: string | null;
  /** Where they are, in world metres, **feet on the ground** — or `null` if they have not said yet. */
  readonly at: { readonly x: number; readonly y: number; readonly z: number } | null;
  /** Which way they face, in radians. */
  readonly yaw: number;
  /** Whether this connection is in the flat debug view (T49). */
  readonly flat: boolean;
  /**
   * 🙌 **The species of animal in their arms, or `null` for empty-handed** —
   * T261, protocol 37.
   *
   * 🔴 **THE KIND, NEVER THE ID.** Everybody needs to draw *a sheep*; nobody
   * needs to know WHICH sheep, and the id belongs to the `creatures` message.
   *
   * ⚠️ **A carried animal is absent from `creatures` altogether**, because it
   * is not standing anywhere. **This is the only way anyone else learns it
   * exists.**
   */
  readonly carrying: string | null;
  /** 🐣 Protocol 49 — how big, if what they carry is a lamb. Absent means grown. */
  readonly carryingLamb?: number | undefined;
  /** 🛏️ Protocol 50 — lying in a bed: `at` is the mattress, `yaw` runs along it. */
  readonly lying?: boolean | undefined;
}

/**
 * 🧍 **One person as somebody else is told about them** — the `players`
 * message's own row.
 *
 * ⚠️ **NAMED, because it was written out twice** — once as
 * {@link playersVisibleTo}'s return type and once as the local it builds — and
 * **two copies of one shape drift the day a field is added to one of them.**
 * 🔴 T261 added `carrying` and found exactly that: the two differed only in
 * indentation, so a single edit could not touch both.
 */
export interface Seen {
  id: string;
  name: string | null;
  x: number;
  y: number;
  z: number;
  yaw: number;
  /** 🙌 What they are carrying, by species, or `null` — T261. */
  carrying: string | null;
  /** 🐣 Protocol 49 — present only for a lamb. */
  carryingLamb?: number;
  /** 🛏️ Protocol 50 — present only for a sleeper. */
  lying?: true;
}

/**
 * 🧍 **Everybody `viewer` should be shown, in no particular order.**
 *
 * 🔴 **Never the viewer themselves.** A client that had to find itself in its
 * own list would eventually get it wrong, and the failure is a body standing
 * inside your own head that follows you everywhere.
 *
 * ⚠️ **Somebody who has not reported a position is left out.** They are
 * connected and have nowhere to be drawn — `at` is `null` until the first
 * `player_state` — and **a body at the world origin is worse than no body**,
 * because it is somebody standing in the sea off Reykjanes.
 *
 * ⚠️ **The flat debug view is excluded in BOTH directions.** It stands every
 * column at a made-up height (T49), so a flat player would appear underground
 * to everyone else and everyone else would appear buried in a floor to them.
 * **Neither of them would be looking at the same world**, which is the whole
 * reason that view exists.
 */
export function playersVisibleTo(viewer: Present, everyone: readonly Present[]): readonly Seen[] {
  if (viewer.flat) return [];
  const out: Seen[] = [];
  for (const other of everyone) {
    if (other.id === viewer.id) continue;
    if (other.flat) continue;
    if (other.at === null) continue;
    /* 🧍 The name rides along with the position rather than being asked for
       separately — T176. ⚠️ **It is passed through UNCHANGED, `null` and
       all.** Substituting a placeholder here would put the lie in the one
       place every client reads it from. */
    out.push({
      id: other.id,
      name: other.name,
      /* 🙌 Passed through unchanged, `null` and all — the same rule the name
         above keeps. T261. */
      carrying: other.carrying,
      /* 🐣 Protocol 49 — only when it is a lamb, so a grown sheep sends nothing new. */
      ...(other.carryingLamb === undefined ? {} : { carryingLamb: other.carryingLamb }),
      /* 🛏️ Protocol 50 — only for a sleeper, so somebody awake sends nothing new. */
      ...(other.lying === true ? { lying: true as const } : {}),
      x: other.at.x,
      y: other.at.y,
      z: other.at.z,
      yaw: other.yaw,
    });
  }
  return out;
}

/**
 * 🔄 **The shortest way round from one angle to another, in radians.**
 *
 * 🔴 **THIS EXISTS BECAUSE A STRAIGHT INTERPOLATION SPINS PEOPLE.** Turning
 * from +179° to −179° is **two degrees**, and a plain `a + (b − a) × t` takes
 * the body **358° the other way** — a person pirouetting on the spot every time
 * they cross south.
 *
 * ⚠️ **Minecraft never meets this bug**, and the reason is worth keeping:
 * **its rotation is one byte, 1/256 of a turn**, and byte arithmetic wraps for
 * free (`Development/Minecraft/16` §8). **We send a float in radians, so the
 * wrap is ours to handle** — and it is these four lines.
 *
 * @returns a value in **(−π, π]** — the signed turn to apply to `from`.
 */
export function shortestTurn(from: number, to: number): number {
  const TWO_PI = Math.PI * 2;
  /* ⚠️ Two `%` operations, not one: JavaScript's `%` keeps the sign of the
     left-hand side, so a single one leaves a negative result for a negative
     difference and the wrap only works in one direction. */
  const diff = (((to - from + Math.PI) % TWO_PI) + TWO_PI) % TWO_PI;
  return diff - Math.PI;
}

/**
 * 🔄 **Part of the way from one angle to another, the short way round.**
 *
 * ⚠️ **The RESULT IS NOT NORMALISED, on purpose.** Turning from 179° to −179°
 * lands on **181°**, which is the same direction. A mesh's `rotation.y` does not
 * care, and **wrapping it back would put the jump back in** — the very thing
 * this function exists to remove.
 *
 * @param t How far, 0…1. ⚠️ **Not clamped**: a caller passing more than 1
 *   overshoots, which is what a frame-rate-independent smoother does on a long
 *   frame and is why this does not quietly correct it. See {@link approach}.
 */
export function turnTowards(from: number, to: number, t: number): number {
  return from + shortestTurn(from, to) * t;
}

/**
 * 📉 **How far to move towards a target this frame, 0…1** — frame-rate
 * independent smoothing.
 *
 * 🔴 **A fixed fraction per frame is a lie about time.** `x += (target − x) *
 * 0.2` moves five times as fast at 300 fps as at 60, so a body that glides on
 * one machine snaps on another and neither is wrong about the code.
 *
 * @param halfLife Seconds to cover **half** the remaining distance.
 * @param dt Seconds since the last frame.
 */
export function approach(halfLife: number, dt: number): number {
  if (halfLife <= 0) return 1;
  return 1 - Math.pow(0.5, dt / halfLife);
}
